import { __awaiter } from "tslib";
import { AppHostAdaptiveCardNovaContextEventingProvider } from '@office-start/app-host-nova-eventing';
import { dialogPromiseHandlers, getHubServices, useAppHostContext, } from '@office-start/app-host-service';
import { hostVersionsInfo } from '@office-start/app-host-types';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useBCAIS, usePinningEligible, } from '@office-start/copilot-route-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useFeatureSettings } from '@office-start/feature-settings-service';
import { useIdentity } from '@office-start/identity-service';
import { launchUrl } from '@office-start/launcher-service';
import { useLocStrings } from '@office-start/loc-service';
import { isBizChatEnabled, useAppDefinitionById, } from '@office-start/m365-apps-service';
import { DialogContentType, FrameContexts, MetaOsAppContainer, } from '@office-start/metaos-hub-sdk';
import { useRemotableRenderer } from '@office-start/remote-module-utility';
import { TaskModuleDialogControl, } from '@office-start/task-module-dialog-control';
import { observeTaskModule, submitTaskModule, } from '@office-start/task-module-dialog-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import React, { createRef, useCallback, useEffect, useState, } from 'react';
import { useMOSCapabilitiesFeatureSettingsTemp } from './hooks/useMOSCapabilitiesFeatureSettingsTemp';
import { createSubPageIdForStoreDialog, formatTaskModuleTitle, logTaskModuleUserAction, } from './utilities';
const dialogSizeRef = createRef();
export const updateTaskModule = (taskModuleSize) => {
    var _a;
    logTaskModuleUserAction('Resize', 'Update_Task_Module_Dialog', 'Edit');
    (_a = dialogSizeRef.current) === null || _a === void 0 ? void 0 : _a.set({
        height: taskModuleSize.height,
        width: taskModuleSize.width,
    });
};
const onCancel = () => {
    if (dialogPromiseHandlers) {
        logTaskModuleUserAction('Click', 'Cancel_Task_Module_Dialog', 'Cancel');
        dialogPromiseHandlers.externalRejector('User cancelled/closed the task module.');
    }
    submitTaskModule();
};
export const TaskModuleDialogComponent = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const TaskModuleDialogControlRenderer = useRemotableRenderer(() => __awaiter(void 0, void 0, void 0, function* () {
        const module = yield import('@office-start/task-module-dialog-control');
        return module.RemotableTaskModuleDialogControl;
    }), TaskModuleDialogControl);
    const { data: officeComponentContext } = useOfficeComponentContext();
    const { data: officeIdentity } = useIdentity() || {};
    const { data: locString } = useLocStrings();
    const capabilitiesFeatureSettings = useMOSCapabilitiesFeatureSettingsTemp();
    const { data: userEntitlements } = useUserEntitlements();
    const featureGates = (_b = (_a = useFeatureGates()) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.result;
    const { data: featureSettings } = useFeatureSettings();
    const isBCAIS = useBCAIS();
    const isPinningEligible = usePinningEligible();
    const [hubServices, setHubServices] = useState();
    const [taskModuleDialog, setTaskModualDialog] = useState(null);
    useEffect(() => {
        const subscription = observeTaskModule().subscribe(setTaskModualDialog);
        return () => {
            subscription.unsubscribe();
        };
    }, []);
    // TODO: results not needed will remove when function signature changes
    const submitTask = useCallback((_results) => {
        submitTaskModule();
    }, []);
    const updateTask = useCallback((dialogSize) => {
        updateTaskModule(dialogSize);
    }, []);
    const { appDefinition } = useAppDefinitionById((_c = taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.appId) !== null && _c !== void 0 ? _c : '');
    const context = useAppHostContext({
        appHostSessionId: (_d = taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.appHostSessionId) !== null && _d !== void 0 ? _d : '',
        appId: (_e = taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.appId) !== null && _e !== void 0 ? _e : '',
        entityId: (_h = (_g = (_f = appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.staticTabs) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.entityId) !== null && _h !== void 0 ? _h : '',
        frameContext: FrameContexts.task,
        isFullScreen: false,
        ringId: capabilitiesFeatureSettings.ringId,
        additionalParams: taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.additionalParams,
    });
    useEffect(() => {
        let isMounted = true;
        const fetchHubServices = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            if (!taskModuleDialog || !appDefinition) {
                return;
            }
            const createdHubServices = yield getHubServices({
                appId: taskModuleDialog.appId,
                appHostSessionId: (_a = taskModuleDialog.appHostSessionId) !== null && _a !== void 0 ? _a : '',
                frameContext: FrameContexts.task,
                userId: (_b = officeIdentity === null || officeIdentity === void 0 ? void 0 : officeIdentity.puid) !== null && _b !== void 0 ? _b : '',
                // startTask is not needed fo task module
                dialogHandlers: {
                    submitTask,
                    updateTask,
                },
                featureGates,
            });
            if (isMounted && createdHubServices) {
                setHubServices(createdHubServices);
            }
        });
        fetchHubServices();
        return () => {
            isMounted = false;
        };
    }, [
        taskModuleDialog,
        taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.appId,
        taskModuleDialog === null || taskModuleDialog === void 0 ? void 0 : taskModuleDialog.appHostSessionId,
        officeIdentity === null || officeIdentity === void 0 ? void 0 : officeIdentity.puid,
        submitTask,
        updateTask,
        appDefinition,
    ]);
    if (!taskModuleDialog ||
        !locString ||
        !appDefinition ||
        !context ||
        !hubServices ||
        !officeIdentity) {
        return null;
    }
    const { taskInfo, dialogType } = taskModuleDialog;
    taskInfo.title = formatTaskModuleTitle((_j = taskInfo.title) !== null && _j !== void 0 ? _j : '', appDefinition.name, dialogType);
    const hasBizChatApp = isBizChatEnabled(featureGates, featureSettings === null || featureSettings === void 0 ? void 0 : featureSettings.result, userEntitlements === null || userEntitlements === void 0 ? void 0 : userEntitlements.result, undefined, officeIdentity, false, isBCAIS, isPinningEligible) &&
        ((_k = featureGates === null || featureGates === void 0 ? void 0 : featureGates.immersiveBizchatM365AppGates) === null || _k === void 0 ? void 0 : _k.immersiveBizchatM365AppPluginsInStoreEnabled);
    const queryParams = new URLSearchParams(window.location.search);
    // Convert queryParams to a plain object
    const paramsObject = {};
    queryParams.forEach((value, key) => {
        paramsObject[key] = value;
    });
    const hideHeader = dialogType !== 'TASKMODULE';
    context.page.subPageId = hideHeader
        ? JSON.stringify(createSubPageIdForStoreDialog(dialogType, paramsObject['templatedAppId'], paramsObject['templateInstanceId'], paramsObject['environment'], paramsObject['source'], hasBizChatApp))
        : context.page.subPageId;
    const fallbackHandler = () => {
        if (!taskInfo.fallbackUrl) {
            // TODO: figure out course of action if no fallback
            return;
        }
        const action = 'OpenInBrowser';
        const linkTarget = '_blank';
        launchUrl(taskInfo.fallbackUrl, { action, linkTarget });
    };
    // if hubServices.nestedAppAuthService is available, recommend the metaos app to use NAA channel
    const isNAAChannelRecommended = !!hubServices.nestedAppAuthService;
    let content;
    {
        if (taskInfo.type == DialogContentType.Card) {
            content = (React.createElement(AppHostAdaptiveCardNovaContextEventingProvider, { card: taskInfo.card, errorFallbackHandler: fallbackHandler }));
        }
        else if (taskInfo.type == DialogContentType.Url &&
            officeComponentContext &&
            officeIdentity) {
            const mosAppContainerProps = {
                hostWindow: window,
                hubServices,
                appDefinition: appDefinition.hubSdkAppDefinition,
                contentUrl: taskInfo.url,
                title: taskInfo.title,
                context,
                hostVersionsInfo: hostVersionsInfo,
                isNAAChannelRecommended: isNAAChannelRecommended,
            };
            content = React.createElement(MetaOsAppContainer, Object.assign({}, mosAppContainerProps));
        }
    }
    return (TaskModuleDialogControlRenderer && (React.createElement(TaskModuleDialogControlRenderer, { taskInfo: taskModuleDialog.taskInfo, onCancel: onCancel, appName: appDefinition.name, locStrings: locString.metaOSHubResources, icon: appDefinition.largeImageUrl, hideHeader: hideHeader, ref: dialogSizeRef }, content)));
};
//# sourceMappingURL=TaskModuleDialogComponent.js.map